import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

import { tryRefreshToken } from '@/lib/api/client'

/** Số lần tự mở lại luồng sau lỗi nặng trước khi báo lỗi cho người xem. */
export const HLS_MAX_RECOVERIES = 2
/** Chờ trước mỗi lần tự mở lại: đủ để máy chủ camera cấp lại chữ ký. */
export const HLS_RECOVERY_DELAY_MS = 1_500

interface Options {
    /** Khung hình đầu tiên đã chạy. */
    onPlaying: () => void
    /** Hết số lần tự mở lại mà vẫn lỗi. */
    onError: () => void
}

/**
 * Phát một luồng HLS vào thẻ `<video>` trả về.
 *
 * Dùng `hls.js` (tải lười, chỉ khi có người mở xem) trên trình duyệt có MSE;
 * Safari iOS không có MSE thì phát HLS sẵn có của trình duyệt. `src` = null là
 * không phát gì — gỡ hẳn trình phát để không còn request nào tới camera.
 *
 * Lỗi nặng (mất mạng, chữ ký của máy chủ camera hết hạn) thì tự mở lại từ
 * playlist gốc tối đa {@link HLS_MAX_RECOVERIES} lần liên tiếp: playlist gốc
 * luôn xin chữ ký mới, nên phần lớn lỗi tự khỏi mà người xem không phải bấm gì.
 * Video chạy lại thì bộ đếm về 0, nên xem lâu vẫn tự hồi phục được.
 *
 * Request của trình phát không đi qua axios, nên không được interceptor tự làm
 * mới access token. Lỗi 401 (token hết hạn giữa lúc xem) thì làm mới token
 * bằng đúng hàm của axios rồi mới mở lại.
 */
export function useHlsPlayer(
    src: string | null,
    options: Options,
): RefObject<HTMLVideoElement | null> {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    // Callback giữ trong ref: call-site thường truyền hàm mới mỗi lần render,
    // đưa vào deps là trình phát bị dựng lại liên tục.
    const callbacks = useRef(options)
    useEffect(() => {
        callbacks.current = options
    }, [options])

    useEffect(() => {
        const video = videoRef.current
        if (!src || !video) return
        // Bản đã thu hẹp kiểu cho các hàm bên dưới (khai báo function được
        // hoist nên TypeScript không giữ phép thu hẹp ở trên cho chúng).
        const media: HTMLVideoElement = video
        const url: string = src

        let disposed = false
        let recoveries = 0
        let retryTimer: ReturnType<typeof setTimeout> | undefined
        let destroyPlayer: (() => void) | undefined

        const onPlaying = () => {
            recoveries = 0
            callbacks.current.onPlaying()
        }
        video.addEventListener('playing', onPlaying)

        function teardown() {
            destroyPlayer?.()
            destroyPlayer = undefined
        }

        function fail(sessionExpired: boolean) {
            teardown()
            if (disposed) return
            if (recoveries >= HLS_MAX_RECOVERIES) {
                callbacks.current.onError()
                return
            }
            recoveries += 1
            const ready = sessionExpired
                ? tryRefreshToken().catch(() => false)
                : Promise.resolve(true)
            void ready.then(() => {
                if (disposed) return
                retryTimer = setTimeout(run, HLS_RECOVERY_DELAY_MS)
            })
        }

        function run() {
            start().catch(() => {
                if (!disposed) callbacks.current.onError()
            })
        }

        async function start() {
            const { default: Hls } = await import('hls.js')
            if (disposed) return

            if (!Hls.isSupported()) {
                if (!media.canPlayType('application/vnd.apple.mpegurl')) {
                    callbacks.current.onError()
                    return
                }
                // Trình phát sẵn có không cho biết mã lỗi: coi như có thể là
                // token hết hạn (làm mới token được gộp chung, rất rẻ).
                const onNativeError = () => fail(true)
                media.addEventListener('error', onNativeError)
                media.src = url
                media.play().catch(() => undefined)
                destroyPlayer = () => {
                    media.removeEventListener('error', onNativeError)
                    media.removeAttribute('src')
                    media.load()
                }
                return
            }

            const hls = new Hls({
                lowLatencyMode: true,
                // Cookie access_token phải đi kèm cả khi API khác origin với
                // app (CORS của server đã AllowCredentials).
                xhrSetup: (xhr) => {
                    xhr.withCredentials = true
                },
                // Xem trực tiếp: không giữ video đã qua trong bộ nhớ.
                backBufferLength: 10,
            })
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) fail(data.response?.code === 401)
            })
            hls.loadSource(url)
            hls.attachMedia(media)
            media.play().catch(() => undefined)
            destroyPlayer = () => hls.destroy()
        }

        run()

        return () => {
            disposed = true
            clearTimeout(retryTimer)
            video.removeEventListener('playing', onPlaying)
            teardown()
        }
    }, [src])

    return videoRef
}
